import React, { useState, useEffect } from 'react'
import {
  useListDataModelSchemaResources,
  useCreateDataModelSchemaResource,
  useDeleteDataModelSchemaResource,
  useDownloadDataModelSchemaResource,
} from '../api'
import {
  DATA_MODEL_SCHEMA_RESOURCE_KINDS,
  DataModelSchemaResourceKind,
} from 'common/types/kraken-core/DataModelSchemaResource'
import { filterValidResources } from '../utils/resourceFilters'
import { Button, Label, Layout, Select, TextField } from '@loadsmart/loadsmart-ui'
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import { toast } from 'react-toastify'
import LSDate, { DATE_FORMAT_MMDDYYYYHHMM } from 'common/Date.helpers'
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Description,
  PictureAsPdf,
  TextFields,
  Code,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Close,
} from '@mui/icons-material'
import FileUploader from 'molecules/FileUploader/FileUploader'
import ResourceEditDialog from 'molecules/ResourceEditDialog'
import { useResourceEdit } from 'hooks/useResourceEdit'
import { isEditableContentType } from 'common/helpers/contentType'

// Helper function to determine content type based on file extension
const getContentTypeFromFile = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase()

  // Map file extensions to content types
  const extensionToContentType: Record<string, string> = {
    x12: 'application/EDI-X12',
    json: 'application/json',
    xml: 'application/xml',
    csv: 'text/csv',
    txt: 'text/plain',
    yaml: 'application/yaml',
    yml: 'application/yaml',
    md: 'text/markdown',
    pdf: 'application/pdf',
  }

  // If we have a mapping for this extension, use it
  if (extension && extensionToContentType[extension]) {
    return extensionToContentType[extension]
  }

  // Otherwise, use the file's type or default to octet-stream
  return file.type || 'application/octet-stream'
}

interface DataModelSchemaResourcesProps {
  dataModelSchemaId: string
  refreshTrigger?: number
  onResourceChange?: () => void
}

interface ResourceFormData {
  name: string
  file: File | null
  content_type: string
  kind: DataModelSchemaResourceKind
}

interface PendingResource {
  id: string
  name: string
  originalName: string
  content_type: string
  kind: DataModelSchemaResourceKind
  created_at: string
  size: number
  isPending: boolean
  file: File
}

export default function DataModelSchemaResources({
  dataModelSchemaId,
  refreshTrigger,
  onResourceChange,
}: DataModelSchemaResourcesProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deleteSelectedResource, setDeleteSelectedResource] = useState<any>(null)

  const {
    isEditDialogOpen,
    selectedResource,
    isLoadingContent,
    handleEditResource,
    closeEditDialog,
    handleSave,
  } = useResourceEdit({
    onEditComplete: () => {
      refetch()
      if (onResourceChange) {
        onResourceChange()
      }
    },
  })
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [pendingResources, setPendingResources] = useState<PendingResource[]>([])
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const [formData, setFormData] = useState<ResourceFormData>({
    name: '',
    file: null,
    content_type: '',
    kind: DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE,
  })

  // API hooks
  const { data: resourcesData, isLoading, refetch } = useListDataModelSchemaResources({
    model_id: dataModelSchemaId,
  })

  const { mutate: createResource, isLoading: isCreating } = useCreateDataModelSchemaResource()
  const { mutate: deleteResource, isLoading: isDeleting } = useDeleteDataModelSchemaResource()
  const { mutate: downloadResource } = useDownloadDataModelSchemaResource()

  // Listen for refresh trigger from parent component
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetch()
    }
  }, [refreshTrigger, refetch])

  const handleFileUploaded = (files: File[]) => {
    if (!files || files.length === 0) {
      return
    }

    setIsProcessingFiles(true)

    try {
      // Add files to uploaded files list
      setUploadedFiles(prevFiles => [...prevFiles, ...files])

      // Create pending resources for immediate display
      const newPendingResources: PendingResource[] = files.map((file, index) => ({
        id: `pending-${Date.now()}-${index}`,
        name: file.name.replace(/\.[^/.]+$/, ''),
        originalName: file.name,
        content_type: getContentTypeFromFile(file),
        kind: formData.kind,
        created_at: new Date().toISOString(),
        size: file.size,
        isPending: true,
        file: file,
      }))

      setPendingResources(prev => [...prev, ...newPendingResources])

      // Auto-populate form data with the first file (for backward compatibility)
      const file = files[0]
      const fileName = file.name.replace(/\.[^/.]+$/, '')

      setFormData(prev => ({
        ...prev,
        file,
        name: fileName,
        content_type: getContentTypeFromFile(file),
      }))
    } catch (error) {
      console.error('Error processing files:', error)
      toast.error('Error processing files')
    } finally {
      setIsProcessingFiles(false)
    }
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setUploadedFiles(prevFiles => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove)

      // Update form data if the removed file was the selected one
      if (formData.file === uploadedFiles[indexToRemove]) {
        const newFile = newFiles[0] || null
        const fileName = newFile ? newFile.name.replace(/\.[^/.]+$/, '') : ''
        setFormData(prev => ({
          ...prev,
          file: newFile,
          name: fileName,
          content_type: newFile ? getContentTypeFromFile(newFile) : '',
        }))
      }

      return newFiles
    })

    // Also remove from pending resources
    setPendingResources(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileName: string, contentType?: string) => {
    // If we have a content-type, use it as the primary source for icon selection
    if (contentType) {
      const lowerContentType = contentType.toLowerCase()

      // Map content types to icons
      if (lowerContentType === 'application/pdf') {
        return <PictureAsPdf sx={{ fontSize: 20, color: '#d32f2f' }} />
      }

      if (lowerContentType === 'application/json' || lowerContentType === 'application/xml') {
        return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
      }

      if (
        lowerContentType === 'text/plain' ||
        lowerContentType === 'text/csv' ||
        lowerContentType === 'text/markdown'
      ) {
        return <TextFields sx={{ fontSize: 20, color: '#1976d2' }} />
      }

      if (lowerContentType === 'application/edi-x12' || lowerContentType === 'application/x12') {
        return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
      }

      if (lowerContentType === 'application/yaml' || lowerContentType === 'text/yaml') {
        return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
      }

      if (lowerContentType.startsWith('image/')) {
        return <Image sx={{ fontSize: 20, color: '#f57c00' }} />
      }

      if (lowerContentType.startsWith('video/')) {
        return <VideoFile sx={{ fontSize: 20, color: '#7b1fa2' }} />
      }

      if (lowerContentType.startsWith('audio/')) {
        return <AudioFile sx={{ fontSize: 20, color: '#c2185b' }} />
      }

      if (
        lowerContentType === 'application/zip' ||
        lowerContentType === 'application/x-zip-compressed' ||
        lowerContentType === 'application/x-rar-compressed' ||
        lowerContentType === 'application/x-7z-compressed' ||
        lowerContentType === 'application/x-tar' ||
        lowerContentType === 'application/gzip'
      ) {
        return <Archive sx={{ fontSize: 20, color: '#ff9800' }} />
      }

      if (lowerContentType === 'application/octet-stream') {
        return <Description sx={{ fontSize: 20, color: '#666' }} />
      }
    }

    // Fallback to file extension if no content-type or no match found
    const extension = fileName.split('.').pop()?.toLowerCase()

    switch (extension) {
      case 'pdf':
        return <PictureAsPdf sx={{ fontSize: 20, color: '#d32f2f' }} />
      case 'txt':
      case 'doc':
      case 'docx':
        return <TextFields sx={{ fontSize: 20, color: '#1976d2' }} />
      case 'json':
      case 'xml':
      case 'yaml':
      case 'yml':
      case 'js':
      case 'css':
        return <Code sx={{ fontSize: 20, color: '#388e3c' }} />
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return <Image sx={{ fontSize: 20, color: '#f57c00' }} />
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
        return <VideoFile sx={{ fontSize: 20, color: '#7b1fa2' }} />
      case 'mp3':
      case 'wav':
      case 'aac':
        return <AudioFile sx={{ fontSize: 20, color: '#c2185b' }} />
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <Archive sx={{ fontSize: 20, color: '#ff9800' }} />
      default:
        return <Description sx={{ fontSize: 20, color: '#666' }} />
    }
  }

  const handleAddResource = () => {
    if (pendingResources.length === 0) {
      toast.error('Please upload at least one file')
      return
    }

    // Validate that all resources have names
    const invalidResources = pendingResources.filter(r => !r.name.trim())
    if (invalidResources.length > 0) {
      toast.error('Please provide names for all resources')
      return
    }

    setIsProcessingFiles(true)

    // Upload each resource sequentially
    const uploadPromises = pendingResources.map(
      resource =>
        new Promise((resolve, reject) => {
          createResource(
            {
              model: dataModelSchemaId,
              name: resource.name,
              file: resource.file,
              content_type: resource.content_type,
              kind: resource.kind,
            },
            {
              onSuccess: () => resolve(true),
              onError: () => reject(new Error(`Failed to upload ${resource.name}`)),
            }
          )
        })
    )

    Promise.all(uploadPromises)
      .then(() => {
        toast.success(`${pendingResources.length} resource(s) added successfully`)
        setIsAddModalOpen(false)
        setFormData({
          name: '',
          file: null,
          content_type: '',
          kind: DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE,
        })
        setUploadedFiles([])
        setPendingResources([])

        // Add a small delay to ensure server has processed the upload
        setTimeout(() => {
          refetch()
          if (onResourceChange) {
            onResourceChange()
          }
        }, 500)
      })
      .catch(error => {
        console.error('Upload error:', error)
        toast.error('Failed to add one or more resources')
      })
      .finally(() => {
        setIsProcessingFiles(false)
      })
  }

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false)
    setFormData({
      name: '',
      file: null,
      content_type: '',
      kind: DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE,
    })
    setUploadedFiles([])
    setPendingResources([])
    setIsProcessingFiles(false)
  }

  const handleDeleteResource = (resource: any) => {
    setDeleteSelectedResource(resource)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (!deleteSelectedResource) return

    deleteResource(deleteSelectedResource.id, {
      onSuccess: () => {
        toast.success('Resource deleted successfully')
        setIsDeleteDialogOpen(false)
        setDeleteSelectedResource(null)
        refetch()
        if (onResourceChange) {
          onResourceChange()
        }
      },
      onError: () => {
        toast.error('Failed to delete resource')
      },
    })
  }

  const handleDownloadResource = (resource: any) => {
    downloadResource(resource.id, {
      onSuccess: blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = resource.name
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      },
      onError: () => {
        toast.error('Failed to download resource')
      },
    })
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (value: string, resource: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getFileIcon(resource.name || '', resource.content_type)}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'kind',
      label: 'Type',
      render: (value: string) => (
        <Chip
          label={value}
          size="small"
          color={value === DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE ? 'primary' : 'error'}
          variant="outlined"
          sx={{
            textTransform: 'capitalize',
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      key: 'content_type',
      label: 'Content Type',
      render: (value: string) => <Typography variant="body2">{value}</Typography>,
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <Typography variant="body2">{LSDate(value)?.format(DATE_FORMAT_MMDDYYYYHHMM)}</Typography>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, resource: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip
            title={
              isEditableContentType(resource.content_type)
                ? 'Edit resource content and properties'
                : 'Edit resource properties (content editing not available for this file type)'
            }
          >
            <IconButton size="small" onClick={() => handleEditResource(resource)}>
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Download">
            <IconButton size="small" onClick={() => handleDownloadResource(resource)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteResource(resource)}
              disabled={isDeleting}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Resources
          </Typography>
        </Box>
        <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
          <AddIcon style={{ marginRight: '8px' }} />
          Add Resource
        </Button>
      </Box>

      <Paper elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell key={`header-${index}`}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {resourcesData?.results && resourcesData.results.length > 0 ? (
              filterValidResources(resourcesData.results).map((resource: any, rowIndex: number) => (
                <TableRow key={`row-${rowIndex}`}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={`cell-${rowIndex}-${colIndex}`}>
                      {column.render
                        ? column.render(resource[column.key], resource)
                        : resource[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No resources found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add Resource Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '70vh' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Add Resource</Typography>
            <IconButton onClick={handleCloseAddModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Layout.Stack space="s">
            <Layout.Stack space="xs">
              <Label htmlFor="resource-kind">Resource Type</Label>
              <Select
                id="resource-kind"
                name="resource-kind"
                options={[
                  { label: 'Knowledge', value: DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE },
                  { label: 'Validation', value: DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION },
                ]}
                value={
                  {
                    value: formData.kind,
                    label:
                      formData.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.KNOWLEDGE
                        ? 'Knowledge'
                        : 'Validation',
                  } as Selectable
                }
                onChange={e => {
                  const value = e.target.value as any
                  setFormData(prev => ({
                    ...prev,
                    kind: value.value as DataModelSchemaResourceKind,
                  }))
                }}
              />
            </Layout.Stack>

            <Layout.Stack space="xs">
              <Label htmlFor="resource-name">Resource Name</Label>
              <TextField
                id="resource-name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter resource name"
              />
            </Layout.Stack>

            <Layout.Stack space="xs">
              <Label htmlFor="resource-file">Resource File</Label>
              <FileUploader handleUpload={handleFileUploaded} multiple={true} />
              {isProcessingFiles && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Processing files...
                </Typography>
              )}
            </Layout.Stack>

            {pendingResources.length > 0 && (
              <Layout.Stack space="xs">
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Rename Resources
                </Typography>
                <Stack spacing={2}>
                  {pendingResources.map((resource, index) => (
                    <Paper
                      key={resource.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {getFileIcon(resource.originalName, resource.content_type)}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            Original: {resource.originalName}
                          </Typography>
                          <Box sx={{ width: '100%', mt: 0.5 }}>
                            <TextField
                              value={resource.name}
                              onChange={e => {
                                setPendingResources(prev =>
                                  prev.map(r =>
                                    r.id === resource.id ? { ...r, name: e.target.value } : r
                                  )
                                )
                              }}
                              placeholder="Enter resource name"
                            />
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPendingResources(prev => prev.filter(r => r.id !== resource.id))
                            setUploadedFiles(prev => prev.filter((_, i) => i !== index))
                          }}
                          sx={{
                            color: '#666',
                            '&:hover': {
                              color: '#d32f2f',
                              backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Layout.Stack>
            )}
          </Layout.Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={handleCloseAddModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddResource}
            disabled={isProcessingFiles || pendingResources.length === 0}
          >
            {isProcessingFiles
              ? 'Adding Resources...'
              : `Add ${pendingResources.length} Resource(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setDeleteSelectedResource(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Resource</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete {deleteSelectedResource?.name}? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeleteDialogOpen(false)
              setDeleteSelectedResource(null)
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Resource Dialog */}
      {selectedResource && (
        <ResourceEditDialog
          open={isEditDialogOpen}
          onClose={closeEditDialog}
          resourceId={selectedResource.id}
          initialName={selectedResource.name}
          initialKind={selectedResource.kind}
          content={selectedResource.content || ''}
          contentType={selectedResource.content_type || 'text/plain'}
          onSave={handleSave}
          isLoadingContent={isLoadingContent}
        />
      )}
    </Stack>
  )
}
